import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Span } from '@opentelemetry/api';
import {
  catchError,
  combineLatest,
  combineLatestWith,
  map,
  of,
  switchMap,
  tap,
  throwError,
  timeout,
  TimeoutError,
} from 'rxjs';

import { getApiErrorId, getApiErrorMessage } from '@ninety/platform/models';
import { AuthenticatedUserResponse } from '@ninety/platform/models/auth/authenticated-user-response';
import { LoginError } from '@ninety/platform/models/auth/login-error.enum';
import { MfaType } from '@ninety/platform/models/auth/mfaType.enum';
import { rememberedEmailStorageKey } from '@ninety/platform/models/login/remembered-email';
import { SessionStorageService, StorageService } from '@ninety/platform/services/services/storage.service';
import { TokenService } from '@ninety/platform/services/services/token.service';

import { appActions } from '../../../app.actions';
import { BaseEffects } from '../../../base.effects';
import { TEMP_PASSWORD_DIALOG } from '../../../tokens/auth-dialog.tokens';
import { SPAN_CONTROL } from '../../../tokens/span-control.token';
import { CurrentPersonStateActions } from '../../current-person/current-person.actions';
import { NotificationActions } from '../../notifications/notification.actions';
import { SpinnerActions } from '../../spinner/spinner-state.actions';
import { TotpActions } from '../auth.actions';
import { AuthStateSelectors } from '../auth.selectors';
import { BECOME_A_PARTNER_AFTER_LOGIN_KEY } from '../become-a-partner-redirect';
import { AmplifyService, loginWorkflowKey } from '../services/amplify.service';
import { CognitoSignInOutputSignInStep } from '../services/auth.types';

import { CognitoLikeError, mapCognitoErrorToMessage } from './cognito-error-mapping';
import { LoginActions } from './login.actions';

/**
 * How long an interactive Cognito call (password sign-in, SSO redirect initiation, MFA challenge
 * confirmation) is given before the member is told the attempt failed. Amplify calls `fetch`
 * directly and returns a promise, so this cannot cancel the underlying request — unsubscribing
 * only abandons the result. That's fine: the goal is that the interface recovers and the member
 * is told, not that the socket closes.
 */
export const SIGN_IN_TIMEOUT_MS = 15_000;

/**
 * A timeout must resolve into the same failure action every other Cognito rejection does, but
 * with a distinguishable, non-credential exception name so telemetry (span attribute) and the
 * member-facing message (cognito-error-mapping.ts) can tell a stall apart from a rejection.
 */
function toSignInFailureError(error: unknown): unknown {
  if (!(error instanceof TimeoutError)) return error;
  return Object.assign(new Error(LoginError.CONNECTION_TIMEOUT), { name: 'SignInTimeoutException' });
}

/**
 * Records the Cognito exception name on the login span so web sign-in failures can be grouped by
 * cause in Datadog. Left absent, not defaulted to a placeholder, when the error carries no name
 * — the span still closes cleanly either way.
 */
function recordCognitoExceptionName(span: Span | undefined, error: unknown): void {
  const exceptionName = (error as CognitoLikeError)?.name;
  if (exceptionName) span?.setAttribute('auth.cognito_exception_name', exceptionName);
}

@Injectable()
export class LoginEffects extends BaseEffects {
  private readonly amplifyService = inject(AmplifyService);
  private readonly tokenService = inject(TokenService);
  private readonly router = inject(Router);
  private readonly storage = inject(StorageService);
  private readonly sessionStorage = inject(SessionStorageService);
  private readonly spanService = inject(SPAN_CONTROL);
  private readonly openTempPassword = inject(TEMP_PASSWORD_DIALOG);

  private api = 'api/v4';

  logInWithPassword$ = this.switchMapEffect({
    action: LoginActions.logInWithPassword,
    switchMapFn: ({ email, password }) => {
      const span = this.spanService.startSpan('auth.login.password', {
        attributes: { email },
      });
      return this.amplifyService.signInWithPassword(email, password).pipe(
        // Amplify checks its cached identity before it considers the submitted credentials. Clear
        // that identity and retry once so an old account cannot be replayed into the Ninety login.
        catchError((error: unknown) => {
          if (!isUserAlreadyAuthenticatedError(error)) return throwError(() => error);

          span?.setAttribute('auth.stale_cognito_session_recovery_attempted', true);
          this.amplifyService.clearLocalCognitoSession();
          return this.amplifyService.signInWithPassword(email, password);
        }),
        timeout(SIGN_IN_TIMEOUT_MS),
        map(signInOutput => {
          this.spanService.closeSpan(span);
          return LoginActions.logInNextStep({ signInOutput });
        }),
        catchError((error: unknown) => {
          const signInFailureError = toSignInFailureError(error);
          recordCognitoExceptionName(span, signInFailureError);
          this.spanService.closeSpan(span, signInFailureError as Error);
          return of(LoginActions.logInWithPasswordFailed({ error: signInFailureError }));
        })
      );
    },
  });

  loginRequireNewPassword$ = this.exhaustMapEffect({
    action: LoginActions.logInNextStep,
    filterFn: ({ signInOutput }) =>
      signInOutput.nextStep.signInStep === CognitoSignInOutputSignInStep.CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED,
    exhaustMapFn: () => {
      return this.openTempPassword({ disableClose: true })
        .afterClosed()
        .pipe(
          switchMap(({ newPassword }) => {
            return this.amplifyService.confirmSignIn(newPassword).pipe(
              map(signInOutput => LoginActions.logInNextStep({ signInOutput })),
              catchError((error: unknown) => of(LoginActions.logInFailed({ error, idToken: '' })))
            );
          })
        );
    },
  });

  // TODO: this effects exising SMS login, need to know if this is acceptable
  // or if we need diff hanlding for old SMS vs new i.e. SMS for totpEnabled company's
  loginNextStepMfaSmtp$ = this.switchMapEffect({
    action: LoginActions.logInNextStep,
    filterFn: ({ signInOutput }) =>
      signInOutput.nextStep.signInStep === CognitoSignInOutputSignInStep.CONFIRM_SIGN_IN_WITH_SMS_CODE ||
      signInOutput.nextStep.signInStep === CognitoSignInOutputSignInStep.CONFIRM_SIGN_IN_WITH_TOTP_CODE ||
      signInOutput.nextStep.signInStep === CognitoSignInOutputSignInStep.CONFIRM_SIGN_IN_WITH_EMAIL_CODE ||
      signInOutput.nextStep.signInStep === CognitoSignInOutputSignInStep.CONTINUE_SIGN_IN_WITH_TOTP_SETUP ||
      signInOutput.nextStep.signInStep === CognitoSignInOutputSignInStep.CONTINUE_SIGN_IN_WITH_MFA_SELECTION,
    switchMapFn: ({ signInOutput }) => {
      if (
        signInOutput.nextStep.signInStep === CognitoSignInOutputSignInStep.CONFIRM_SIGN_IN_WITH_TOTP_CODE ||
        signInOutput.nextStep.signInStep === CognitoSignInOutputSignInStep.CONFIRM_SIGN_IN_WITH_EMAIL_CODE ||
        signInOutput.nextStep.signInStep === CognitoSignInOutputSignInStep.CONFIRM_SIGN_IN_WITH_SMS_CODE
      ) {
        let mfaType: MfaType;
        switch (signInOutput.nextStep.signInStep) {
          case CognitoSignInOutputSignInStep.CONFIRM_SIGN_IN_WITH_TOTP_CODE:
            mfaType = MfaType.TOTP;
            break;
          case CognitoSignInOutputSignInStep.CONFIRM_SIGN_IN_WITH_EMAIL_CODE:
            mfaType = MfaType.EMAIL;
            break;
          case CognitoSignInOutputSignInStep.CONFIRM_SIGN_IN_WITH_SMS_CODE:
            mfaType = MfaType.SMS;
            break;
        }
        return of(TotpActions.loginDisplayMFAChallenge({ mfaType }));
      } else if (signInOutput.nextStep.signInStep === CognitoSignInOutputSignInStep.CONTINUE_SIGN_IN_WITH_TOTP_SETUP) {
        return of(TotpActions.loginSetupTotp());
      } else if (
        signInOutput.nextStep.signInStep === CognitoSignInOutputSignInStep.CONTINUE_SIGN_IN_WITH_MFA_SELECTION
      ) {
        const allowedMFATypes = signInOutput.nextStep.allowedMFATypes || [];
        if (allowedMFATypes.length > 0) {
          const mfaType = allowedMFATypes[0] as MfaType;
          const span = this.spanService.startSpan('auth.login.mfa_selection', { attributes: { mfaType } });
          return this.amplifyService.confirmSignIn(mfaType).pipe(
            timeout(SIGN_IN_TIMEOUT_MS),
            tap(() => localStorage.setItem('shouldResetPreferredMfa', 'true')),
            map(signInOutput => {
              this.spanService.closeSpan(span);
              return LoginActions.logInNextStep({ signInOutput });
            }),
            catchError((error: unknown) => {
              const signInFailureError = toSignInFailureError(error);
              recordCognitoExceptionName(span, signInFailureError);
              this.spanService.closeSpan(span, signInFailureError as Error);
              return of(LoginActions.logInFailed({ error: signInFailureError, idToken: '' }));
            })
          );
        } else {
          return of(LoginActions.logInFailed({ error: 'No MFA types available contact support', idToken: '' }));
        }
      }
      return of(appActions.noop());
    },
  });

  loginNextStepDone$ = this.switchMapEffect({
    action: LoginActions.logInNextStep,
    filterFn: ({ signInOutput }) => signInOutput.nextStep.signInStep === CognitoSignInOutputSignInStep.DONE,
    switchMapFn: () =>
      this.amplifyService.getTokens().pipe(
        map(tokens => {
          const idToken = tokens?.idToken?.toString();
          return idToken ? LoginActions.logInWithToken({ idToken }) : LoginActions.noCognitoTokensFound();
        })
      ),
  });

  noCognitoTokensFound$ = this.mapEffect({
    action: LoginActions.noCognitoTokensFound,
    mapFn: () =>
      LoginActions.logInWithPasswordFailed({
        error: new Error('Cognito completed sign-in without returning an ID token'),
      }),
  });

  getCognitoUserAttributes$ = this.switchMapEffect({
    action: LoginActions.logInSuccess,
    switchMapFn: () =>
      this.amplifyService.getCurrentUser().pipe(
        combineLatestWith(this.amplifyService.getUserAttributes(), this.amplifyService.fetchMfaPreference()),
        map(([, attributes, mfaPreference]) => LoginActions.setCognitoUserAttributes({ attributes, mfaPreference })),
        catchError(() => of(appActions.noop()))
      ),
  });

  loginFailed$ = this.mapEffect({
    action: LoginActions.displayGeneralAuthError,
    mapFn: () =>
      NotificationActions.showErrorToast({
        message: 'There was an error while logging you again.  Please try again.',
      }),
  });

  logInWithPasswordFailed$ = this.mapEffect({
    action: LoginActions.logInWithPasswordFailed,
    mapFn: action => NotificationActions.showError({ message: mapCognitoErrorToMessage(action.error), title: '' }),
  });

  fetchNinetyToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoginActions.logInWithToken),
      concatLatestFrom(() => [
        this.store.select(AuthStateSelectors.rememberMe),
        this.store.select(AuthStateSelectors.loginEmailAddress),
      ]),
      switchMap(([{ idToken }, rememberMe, email]) => {
        const span = this.spanService.startSpan('auth.login.token', {
          attributes: { hasIdToken: !!idToken },
        });
        const payload: { idToken: string; rememberMe?: boolean } = { idToken };
        if (rememberMe !== undefined) {
          payload.rememberMe = rememberMe;
        }
        return this.http.post<AuthenticatedUserResponse>(`${this.api}/Login`, payload).pipe(
          map(response => {
            this.tokenService.saveTokens(response.tokens);
            // Last point where rememberMe/emailAddress are live (logInSuccess clears both; reducers
            // run before effects). No else — unchecking deletes the key in LoginFacade. Both SSO
            // providers redirect the whole page, so they land here with a fresh store and skip this;
            // revisit if either becomes a popup flow. try/catch: this is the only *new* key written
            // on the login path, so at quota it throws where the surrounding overwrites don't.
            if (rememberMe && email) {
              try {
                this.storage.set(rememberedEmailStorageKey, email);
              } catch {
                // Best-effort — never fail a login over the remembered email.
              }
            }
            this.spanService.closeSpan(span);
            return LoginActions.logInSuccess({ response, isMicrosoft: false });
          }),
          catchError((error: unknown) => {
            this.spanService.closeSpan(span, error as Error);
            return of(LoginActions.logInFailed({ error, idToken }));
          })
        );
      })
    )
  );

  loginToNinetyFailed$ = this.mapEffect({
    action: LoginActions.logInFailed,
    mapFn: ({ error, idToken }) => {
      const apiMessage = getApiErrorMessage(error);

      if (apiMessage === LoginError.NO_PERSON || apiMessage === LoginError.UNAUTHORIZED) {
        // Do not carry a Cognito identity that Ninety rejected into onboarding or the next login.
        this.amplifyService.clearLocalCognitoSession();
        // The Microsoft code paths dispatch this with no idToken, so only the prefill can depend on one.
        const email = idToken ? this.tokenService.decodeToken<{ email?: string }>(idToken)?.email : undefined;
        this.router.navigate(['/sign-up'], email ? { queryParams: { email } } : undefined);

        return NotificationActions.showError({
          message: 'No Ninety account found for that email. Please sign up.',
          title: '',
        });
      }

      const errorId = getApiErrorId(error);
      // Must stay string-only: the bug being fixed here was passing the whole HttpErrorResponse as a message.
      const message =
        apiMessage ??
        (typeof error === 'string' ? error : undefined) ??
        (error instanceof Error ? error.message : undefined) ??
        'An error occurred while logging in. Please try again.';
      return NotificationActions.showError({
        message: errorId ? `${message} (Reference: ${errorId})` : message,
        title: '',
      });
    },
  });

  logInWithSso$ = this.switchMapEffect({
    action: LoginActions.logInWithSso,
    switchMapFn: ({ provider, workflow }) => {
      const span = this.spanService.startSpan('auth.login.sso', {
        attributes: { provider, workflow },
      });
      const queryParams = new URLSearchParams(window.location.search);
      const redirectUrl = queryParams.get('redirectUrl');
      if (redirectUrl) this.storage.set('redirectUrl', redirectUrl);
      this.sessionStorage.set(loginWorkflowKey, workflow);

      return this.amplifyService.signInWithRedirect(provider).pipe(
        catchError((error: unknown) => {
          if (!isUserAlreadyAuthenticatedError(error)) return throwError(() => error);

          span?.setAttribute('auth.stale_cognito_session_recovery_attempted', true);
          this.amplifyService.clearLocalCognitoSession();
          return this.amplifyService.signInWithRedirect(provider);
        }),
        timeout(SIGN_IN_TIMEOUT_MS),
        // This map doesn't get processed.  We redirect away and come back.  Just need this for the sake of the effect.
        map(() => {
          this.spanService.closeSpan(span);
          return LoginActions.logInWithSsoResponse({ responseUrl: '', workflow });
        }),
        catchError((error: unknown) => {
          const signInFailureError = toSignInFailureError(error);
          recordCognitoExceptionName(span, signInFailureError);
          this.spanService.closeSpan(span, signInFailureError as Error);
          const errorName = (signInFailureError as { name?: string })?.name ?? 'UnknownError';
          return of(LoginActions.logInWithSsoFailed({ error: errorName, errorDescription: errorName }));
        })
      );
    },
  });

  loginWithSsoResponse_with_idtoken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoginActions.logInWithSsoResponse),
      switchMap(({ workflow }) =>
        this.amplifyService.getTokens().pipe(
          map(tokens => {
            let idToken = tokens ? tokens.idToken.toString() : '';
            if (!idToken) {
              const queryParams = new URLSearchParams(window.location.hash);
              idToken = queryParams.get('id_token');
            }
            // Clear the URL fragment to prevent "sticky" SSO params from re-triggering SSO handling on refresh/navigation
            clearUrlFragment();
            return { idToken, workflow };
          })
        )
      ),
      map(({ idToken, workflow }) => {
        this.sessionStorage.delete(loginWorkflowKey);
        this.storage.delete(loginWorkflowKey);
        return LoginActions.logInWithSsoSuccess({ idToken, workflow });
      })
    )
  );

  loginWithSsoResponse_with_error$ = this.mapEffect({
    action: LoginActions.logInWithSsoResponse,
    filterFn: ({ responseUrl }) => responseUrl.toLocaleLowerCase().includes('error'),
    mapFn: ({ responseUrl }) => {
      this.sessionStorage.delete(loginWorkflowKey);
      this.storage.delete(loginWorkflowKey);
      const error = extractParameter(responseUrl, 'error');
      const errorDescription = extractParameter(responseUrl, 'error_description');
      // Clear the URL fragment to prevent "sticky" SSO params from re-triggering SSO handling on refresh/navigation
      clearUrlFragment();
      return LoginActions.logInWithSsoFailed({ error, errorDescription });
    },
  });

  displayLoginError$ = this.mapEffect({
    action: LoginActions.logInWithSsoFailed,
    mapFn: ({ error }) => {
      // A timed-out SSO redirect must read as a stalled connection, not a credential problem —
      // the generic message below reads as the latter.
      const message =
        error === 'SignInTimeoutException'
          ? LoginError.CONNECTION_TIMEOUT
          : 'Please try logging in with username and password. If the issue persists, please contact support.';
      return NotificationActions.showError({ message, title: 'Login error' });
    },
  });

  logInWithSsoSuccess_Login$ = this.mapEffect({
    action: LoginActions.logInWithSsoSuccess,
    filterFn: ({ workflow }) => workflow === 'LOGIN',
    mapFn: ({ idToken }) => LoginActions.logInWithToken({ idToken }),
  });

  loginSuccess_setCurrentPerson$ = this.mapEffect({
    action: LoginActions.logInSuccess,
    mapFn: ({ response: { person } }) => CurrentPersonStateActions.setPerson({ person }),
  });

  loginSuccess_checkMfaRequirement$ = this.switchMapEffect({
    action: LoginActions.logInSuccess,
    switchMapFn: ({ response, isMicrosoft }) => {
      // Orphaned person: person exists but has no active users/companies
      // Redirect to create-company so they can create a new company
      if (response?.userCount === 0) {
        this.router.navigate(['/create-company']);
        return of(appActions.noop());
      }

      if (isMicrosoft) return of(LoginActions.redirectToApp({ response }));

      return this.amplifyService.fetchMfaPreference().pipe(
        // Microsoft SSO won't have Cognito info, default to SSO enabled false
        catchError(() => of({ enabled: [] })),
        map(mfaPreference => {
          const userHasMfaEnabled = mfaPreference.enabled && mfaPreference.enabled.length > 0;
          if (response.mfaSetupRequired && !userHasMfaEnabled) {
            return LoginActions.logInSetupMfa();
          }
          return LoginActions.redirectToApp({ response });
        })
      );
    },
  });

  logInSuccess_checkToResetPreferredMfa$ = this.switchMapEffect({
    action: LoginActions.logInSuccess,
    switchMapFn: () => {
      const shouldResetPreferredMfa = localStorage.getItem('shouldResetPreferredMfa') === 'true';

      localStorage.removeItem('shouldResetPreferredMfa');

      return combineLatest([
        this.amplifyService.fetchMfaPreference().pipe(catchError(() => of({ enabled: [] }))),
        this.amplifyService.getUserAttributes().pipe(catchError(() => of({}))),
      ]).pipe(
        switchMap(([cognitoMfaPreference, cognitoAttributes]) => {
          const userIsSsoMicrosoft = Object.keys(cognitoAttributes).length === 0;
          if (!userIsSsoMicrosoft) {
            if (shouldResetPreferredMfa) {
              let mfaType: MfaType = MfaType.EMAIL; // default
              if (shouldResetPreferredMfa && cognitoMfaPreference.enabled?.includes(MfaType.TOTP)) {
                mfaType = MfaType.TOTP;
              } else if (shouldResetPreferredMfa && cognitoMfaPreference.enabled?.includes(MfaType.SMS)) {
                mfaType = MfaType.SMS;
              } else if (shouldResetPreferredMfa && cognitoMfaPreference.enabled?.includes(MfaType.EMAIL)) {
                mfaType = MfaType.EMAIL;
              }
              return of(TotpActions.updatePreferredMfa({ mfaType }));
            }
          }
        }),
        catchError(() => of(appActions.noop()))
      );
    },
  });

  redirectToApp_fetchTier1Data$ = this.mapEffect({
    action: LoginActions.redirectToApp,
    mapFn: () => appActions.fetchTier1Data(),
  });

  redirectToApp_navigateToRedirectUrl$ = this.tapEffect({
    action: LoginActions.redirectToApp,
    tapFn: ({ response }) => {
      if (response?.userCount === 0) {
        this.router.navigate(['/', 'create-company']);
      }
      // DEV-25231: partner-signup PERSON_EXISTS routed here via login. Send them to
      // /become-a-partner and consume the flag so subsequent logins behave normally.
      if (this.sessionStorage.get(BECOME_A_PARTNER_AFTER_LOGIN_KEY)) {
        this.sessionStorage.delete(BECOME_A_PARTNER_AFTER_LOGIN_KEY);
        this.router.navigate(['/become-a-partner']);
        return;
      }
      const queryParams = new URLSearchParams(window.location.search);
      const redirectUrlFromQuery = queryParams.get('redirectUrl');
      const redirectFromStorage = this.storage.get('redirectUrl');

      const redirectUrl = redirectUrlFromQuery || redirectFromStorage || '/home';

      this.storage.delete('redirectUrl');
      try {
        const url = new URL(redirectUrl, window.location.origin);
        const fragment = url.hash.slice(1);
        this.router.navigate([url.pathname], {
          queryParams: Object.fromEntries(url.searchParams),
          ...(fragment ? { fragment } : {}),
        });
      } catch {
        this.router.navigate(['/home']);
      }
    },
  });

  startSpinner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        LoginActions.logInWithPassword,
        LoginActions.logInWithMicrosoft,
        LoginActions.logInWithSso,
        appActions.fetchTier1Data,
        appActions.fetchTier2Data
      ),
      map(({ type }) => SpinnerActions.startPrimary({ source: type }))
    )
  );

  stopSpinner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        LoginActions.logInWithPasswordFailed,
        LoginActions.logInWithSsoFailed,
        LoginActions.logInWithMicrosoftFailed,
        LoginActions.logInFailed
      ),
      map(({ type }) => SpinnerActions.stopPrimary({ source: type }))
    )
  );
}

function isUserAlreadyAuthenticatedError(error: unknown): boolean {
  return (error as { name?: string })?.name === 'UserAlreadyAuthenticatedException';
}

function extractParameter(urlParam: string, queryStringParam: string) {
  const url = new URL(urlParam);
  const hash = url.hash;
  const params = hash.split('&');
  const param = params.find(param => param.includes(queryStringParam));
  if (param) {
    const paramValue = param.split('=')[1];
    return paramValue;
  }
  return null;
}

/**
 * Clears the URL hash/fragment to prevent "sticky" SSO params (e.g. error_description)
 * from re-triggering SSO handling on refresh/navigation.
 */
function clearUrlFragment(): void {
  try {
    if (typeof window === 'undefined') return;
    const newUrl = `${window.location.pathname}${window.location.search}`;
    window.history.replaceState(window.history.state, '', newUrl);
  } catch {
    // no-op: best-effort cleanup only
  }
}
